import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Archive, ArrowLeft, Loader2, RotateCcw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Pagination } from "@/components/Pagination";
import { TableSkeleton } from "@/components/Skeleton";
import { api, apiError } from "@/lib/api";

const PAGE_SIZE = 25;

export default function ArchivedCratesPage() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(params.get("q") || "");
  const [target, setTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState(null);

  const page = Math.max(1, parseInt(params.get("page") || "1"));
  const q = params.get("q") || "";
  const clientId = params.get("client") || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        api.get("/crates", {
          params: {
            archived_only: true,
            q: q || undefined,
            client: clientId || undefined,
            page,
            page_size: PAGE_SIZE,
          },
        }),
        api.get("/clients"),
      ]);
      setData(pRes.data);
      setClients(cRes.data || []);
    } finally { setLoading(false); }
  }, [page, q, clientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSearchInput(q); }, [q]);

  function applySearch(e) {
    e?.preventDefault();
    const next = new URLSearchParams(params);
    if (searchInput.trim()) next.set("q", searchInput.trim()); else next.delete("q");
    next.set("page", "1");
    setParams(next);
  }

  function changeClient(value) {
    const next = new URLSearchParams(params);
    if (value) next.set("client", value); else next.delete("client");
    next.set("page", "1");
    setParams(next);
  }

  async function confirmRestore() {
    if (!target) return;
    setRestoring(true); setError(null);
    try {
      await api.post(`/crates/${target.id}/archive`, { archived: false });
      setTarget(null);
      await load();
    } catch (err) { setError(apiError(err)); } finally { setRestoring(false); }
  }

  return (
    <div className="space-y-6">
      <Link to="/crates" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-crates">
        <ArrowLeft className="h-4 w-4" /> Back to Wooden Crates
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Archive className="h-6 w-6 text-slate-500" /> Archived Wooden Crates
          </h2>
          <p className="text-muted-foreground">
            {data.total} archived crate{data.total === 1 ? "" : "s"} — restore any item to make it active again.
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <form onSubmit={applySearch} className="flex flex-wrap items-center gap-2">
            <Input
              data-testid="archived-crates-search-input"
              placeholder="Search by crate ID or tracking number…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="max-w-sm"
            />
            <select
              data-testid="archived-crates-client-filter"
              className="flex h-10 w-56 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={clientId}
              onChange={(e) => changeClient(e.target.value)}
            >
              <option value="">All organizations</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <Button type="submit" size="sm" data-testid="archived-crates-search-btn">Search</Button>
            {(q || clientId) && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setParams({})} data-testid="archived-crates-clear-btn">
                Clear
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <TableSkeleton rows={6} cols={6} />
          ) : data.items.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Crate ID</th>
                    <th className="px-4 py-3 font-medium">Organization</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Location</th>
                    <th className="px-4 py-3 font-medium">Archived (Updated)</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((c) => (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`archived-crate-row-${c.id}`}>
                      <td className="px-4 py-3">
                        <Link to={`/crates/${c.id}`} className="font-medium text-primary hover:underline">{c.crate_code}</Link>
                      </td>
                      <td className="px-4 py-3">{c.client_name || "—"}</td>
                      <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                      <td className="px-4 py-3">{c.current_location || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{new Date(c.updated_at).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setTarget(c); setError(null); }}
                          data-testid={`restore-crate-btn-${c.id}`}
                        >
                          <RotateCcw className="mr-1 h-3.5 w-3.5" /> Restore
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 p-10 text-center text-muted-foreground">
              <Archive className="h-5 w-5" />
              <p>No archived crates match your search.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Pagination page={page} pageSize={PAGE_SIZE} total={data.total} />

      <AlertDialog open={!!target} onOpenChange={(o) => { if (!o) { setTarget(null); setError(null); } }}>
        <AlertDialogContent data-testid="restore-crate-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this crate?</AlertDialogTitle>
            <AlertDialogDescription>
              {target && (
                <>
                  <span className="font-medium text-slate-900">{target.crate_code}</span>
                  {target.client_name ? <> · {target.client_name}</> : null}
                  {" "}will be moved back to the active Wooden Crates list. You can archive it again at any time.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoring} data-testid="restore-crate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmRestore(); }} disabled={restoring} data-testid="restore-crate-confirm">
              {restoring && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Yes, restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
